import { MapPin, Building2, Search } from 'lucide-react'
import { useNearbyVenues, useRequestVenue, useOwnVendorVenues } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { SectionNav, type PageSection } from '@/components/ui/section-nav'
import { FormError } from '@/components/ui/form-error'
import { VENDOR_SERVICE_LABELS } from '@lib/enums/vendor-service.enum'

/** Discover venues within the vendor's travel radius and request to serve them. */
function FindVenues() {
  const query = useNearbyVenues()
  const request = useRequestVenue()

  if (query.isPending) {
    return (
      <LoadingState label="Finding venues…" />
    )
  }
  if (query.isError) {
    return (
      <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    )
  }
  if (query.data.venues.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-muted">
        No venues within your radius yet. Make sure your business address is complete and your travel radius is set on
        your Settings page.
      </p>
    )
  }
  return (
    <div className="space-y-3">
      {/* A refusal (the venue closed to new vendors, the email not yet confirmed,
          the day's limit) says why, rather than the button doing nothing. */}
      {request.isError ? (
        <FormError>
          {requestErrorMessage(request.error)}
        </FormError>
      ) : null}
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {query.data.venues.map((v) => (
          <li key={v.venueId} className="flex items-center gap-3 px-4 py-3">
            <MapPin className="h-4 w-4 shrink-0 text-ink-muted" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{v.name}</p>
              <p className="truncate text-xs text-ink-muted">
                {v.distanceMiles} mi away{v.address ? ` · ${v.address}` : ''}
              </p>
            </div>
            {v.status === 'active' ? (
              <span className="text-xs font-medium text-ok-ink">Serving</span>
            ) : v.status === 'pending' ? (
              <span className="text-xs text-ink-muted">Requested</span>
            ) : (
              <Button size="sm" variant="outline" disabled={request.isPending} onClick={() => request.mutate({ venueId: v.venueId })}>
                Request to serve
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Venues the vendor actively serves, with the services each has approved. */
function LinkedVenues() {
  const query = useOwnVendorVenues()

  if (query.isPending) {
    return (
      <LoadingState label="Loading venues…" />
    )
  }
  if (query.isError) {
    return (
      <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    )
  }
  if (query.data.venues.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-muted">
        You&rsquo;re not serving any venues yet. Find one under <strong>Find venues</strong> and request to serve it.
      </p>
    )
  }
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
      {query.data.venues.map((v) => (
        <li key={v.venueId} className="flex items-start gap-3 px-4 py-3">
          <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{v.name}</p>
            <p className="truncate text-xs text-ink-muted">
              {v.tenantName}
              {v.address ? ` · ${v.address}` : ''}
            </p>
            <div className="mt-1.5">
              {v.approvedServices.length === 0 ? (
                <span className="text-xs text-ink-muted">No services approved here yet</span>
              ) : (
                <span className="flex flex-wrap gap-1">
                  {v.approvedServices.map((s) => (
                    <span key={s} className="rounded bg-past-soft px-1.5 py-0.5 text-xs text-ink">
                      {VENDOR_SERVICE_LABELS[s]}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

/** A vendor's venues: find new ones to serve, and see the ones they already serve
 * with the services each venue has approved. */
export function VendorVenuesPage() {
  const sections: PageSection[] = [
    {
      id: 'find',
      label: 'Find venues',
      icon: Search,
      description: 'Venues within your travel radius. Request to serve one; the venue’s team decides.',
      content: <FindVenues />,
    },
    {
      id: 'linked',
      label: 'Serving',
      icon: Building2,
      description: 'Venues you serve, and the services each has approved for you.',
      content: <LinkedVenues />,
    },
  ]

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader
        title="Venues"
        description="Find venues to serve and manage the ones you already do."
      />
      <SectionNav sections={sections} ariaLabel="Vendor venues" />
    </PageContainer>
  )
}
